import React, { FC, useCallback, useRef, useState } from "react";
import { Box, Button, CircularProgress, Chip, Menu, MenuItem, Tooltip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskIcon from "@mui/icons-material/Task";
import IosShareIcon from "@mui/icons-material/IosShare";
import {
  GkvEditWorkspaceProps,
  DirectBillingRxDto,
  GkvBillEditDto,
  TestType,
  TestTypeData,
} from "../types/interface";
import { useGkvEditBills } from "../hooks/useGkvEditBills";
import { useGkvEditActions } from "../hooks/useGkvEditActions";
import { useAutoSaveNote } from "../hooks/useAutoSaveNote";
import { useEditStepper } from "../hooks/useEditStepper";
import { useEditValidation } from "../hooks/useEditValidation";
import { useTaxieren } from "../hooks/useTaxieren";
import ExportResultsDialog from "../dialogs/ExportResultsDialog";
import TestTypeDialog from "../dialogs/TestTypeDialog";
import SpotlightSearch from "../../common/components/SpotlightSearch";
import { DeleteConfirmationDialog } from "../../common/exports";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import { useApi } from "../../../../../../api/ApiProvider";
import { GkvBillExportResultDto } from "../../../../../../api-client";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { colors } from "../../GKVNew/styles/tokens";
import GkvEditHeaderActions from "./GkvEditHeaderActions";
import GkvEditSummary from "./GkvEditSummary";
import GkvEditBulkToolbar from "./GkvEditBulkToolbar";
import GkvEditBillCard from "./GkvEditBillCard";
import GkvEditPrintOverlay from "./GkvEditPrintOverlay";

/**
 * Left-panel workspace for the GKV Edit Bill flow.
 *
 * State hub — consumes all hooks and distributes props to children.
 * Pattern mirrors GkvBillWorkspace from GKVNew.
 */
const GkvEditWorkspace: FC<GkvEditWorkspaceProps> = ({
  billIds,
  onRowClick,
  onRxDelete,
  onClose,
}) => {
  const { transaction } = useApi();

  // ── Data Layer ──
  const {
    bills,
    isLoading,
    removeBill,
    dismissBill,
    removeRx,
    bulkRemoveRxs,
    addRx,
    updateBillLocally,
  } = useGkvEditBills(billIds);

  // ── Actions Layer ──
  const { isPrinting, isExporting, isSaving, printProgress, printAllBatch, exportAll, saveAll } =
    useGkvEditActions();

  // ── Validation ──
  const { validationMap, isValidating, validationProgress } = useEditValidation(bills);

  // ── Notes ──
  const [notes, setNotes] = useState<Record<string, string>>(() => {
    // Initialize from bill data
    const initial: Record<string, string> = {};
    bills.forEach((b) => {
      initial[b.id] = typeof b.notes === "string" ? b.notes : "";
    });
    return initial;
  });
  const { updateNote, flushAll } = useAutoSaveNote(bills);

  const handleNoteChange = useCallback(
    (billId: string, value: string) => {
      setNotes((prev) => ({ ...prev, [billId]: value }));
      updateNote(billId, value);
    },
    [updateNote],
  );

  // ── Taxieren ──
  const handleTaxierenStatusChange = useCallback(
    (rxId: string, isTaxieren: boolean) => {
      // Find bill containing this Rx and patch locally
      const bill = bills.find((b) => b.rxs.some((rx) => rx.id === rxId));
      if (!bill) return;
      updateBillLocally(bill.id, {
        rxs: bill.rxs.map((rx) => (rx.id === rxId ? { ...rx, isTaxieren } : rx)),
      });
    },
    [bills, updateBillLocally],
  );
  const taxieren = useTaxieren(handleTaxierenStatusChange);

  // ── Selection State ──
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [selectedRxIds, setSelectedRxIds] = useState<Set<string>>(new Set());

  // ── Menu State ──
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRx, setMenuRx] = useState<DirectBillingRxDto | null>(null);
  const [menuBillId, setMenuBillId] = useState<string | null>(null);

  // ── Dialog State ──
  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSoftDeleteDialog, setOpenSoftDeleteDialog] = useState(false);
  const [openCancelTaxierenDialog, setOpenCancelTaxierenDialog] = useState(false);
  const [openTestTypeDialog, setOpenTestTypeDialog] = useState(false);
  const [testToggleChecked, setTestToggleChecked] = useState(false);
  const [testTypeData, setTestTypeData] = useState<TestTypeData>({ type: TestType.GLOBAL });
  const [isPrintCompleted, setIsPrintCompleted] = useState(false);
  const [exportResults, setExportResults] = useState<GkvBillExportResultDto[] | null>(null);

  // Track Rx snapshot at print time — if Rxs change, PDFs are outdated
  const printedRxSnapshotRef = useRef<string | null>(null);
  const currentRxSignature = bills
    .flatMap((b) => b.rxs ?? [])
    .map((rx) => rx.id)
    .sort()
    .join(",");

  // Reset print status when bills change after printing
  if (isPrintCompleted && printedRxSnapshotRef.current !== currentRxSignature) {
    setIsPrintCompleted(false);
  }

  // Versenden enabled only in production OR when test type is INTERNAL
  const isVersendenEnabled =
    process.env.REACT_APP_INSTANCE_NAME === "production" || testTypeData.type === TestType.INTERNAL;

  // ── Stepper (depends on isPrintCompleted) ──
  const { activeStep } = useEditStepper(bills, isPrintCompleted);

  // ── Bulk Selection ──
  const toggleRxSelection = useCallback((rxId: string) => {
    setSelectedRxIds((prev) => {
      const next = new Set(prev);
      if (next.has(rxId)) next.delete(rxId);
      else next.add(rxId);
      return next;
    });
  }, []);

  const clearRxSelection = useCallback(() => setSelectedRxIds(new Set()), []);

  const handleBulkDelete = useCallback(async () => {
    await bulkRemoveRxs(selectedRxIds);
    // Notify parent about deleted Rxs
    selectedRxIds.forEach((rxId) => onRxDelete(rxId));
    clearRxSelection();
  }, [selectedRxIds, bulkRemoveRxs, onRxDelete, clearRxSelection]);

  // ── Row Click ──
  const handleRowClick = useCallback(
    (rx: DirectBillingRxDto) => {
      setSelectedRxId(rx.id);
      onRowClick(rx);
    },
    [onRowClick],
  );

  // ── Context Menu ──
  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, rx: DirectBillingRxDto) => {
      setAnchorEl(event.currentTarget);
      setMenuRx(rx);
      // Find which bill this Rx belongs to
      const bill = bills.find((b) => b.rxs.some((r) => r.id === rx.id));
      setMenuBillId(bill?.id || null);
    },
    [bills],
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ── Delete Rx ──
  const handleDeleteClick = () => {
    if (!menuRx || !menuBillId) return;
    const bill = bills.find((b) => b.id === menuBillId);
    if (bill && bill.rxs.length === 1) {
      // Last Rx → soft delete entire bill
      setOpenSoftDeleteDialog(true);
    } else {
      setOpenDeleteDialog(true);
    }
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (menuRx && menuBillId) {
      await removeRx(menuBillId, menuRx.id);
      onRxDelete(menuRx.id);
    }
    setOpenDeleteDialog(false);
    setMenuRx(null);
    setMenuBillId(null);
  };

  const handleSoftDeleteConfirm = async () => {
    if (menuBillId) {
      await removeBill(menuBillId);
      // If no bills remain, close
      if (bills.length <= 1) {
        onClose(true);
      }
    }
    setOpenSoftDeleteDialog(false);
    setMenuRx(null);
    setMenuBillId(null);
  };

  // ── Taxieren from Menu ──
  const handleTaxierenClick = () => {
    if (!menuRx || !menuBillId) return;
    const bill = bills.find((b) => b.id === menuBillId);
    if (bill) taxieren.printTaxieren(menuRx, bill);
    setAnchorEl(null);
  };

  const handleCancelTaxierenClick = () => {
    setOpenCancelTaxierenDialog(true);
    setAnchorEl(null);
  };

  const handleCancelTaxierenConfirm = async () => {
    if (menuRx) {
      await taxieren.cancelTaxieren(menuRx.id);
    }
    setOpenCancelTaxierenDialog(false);
    setMenuRx(null);
  };

  // ── Dismiss bill from session (UI only, no backend delete) ──
  const handleRemoveAll = useCallback(
    (billId: string) => {
      dismissBill(billId);
      if (bills.length <= 1) onClose(false);
    },
    [dismissBill, bills.length, onClose],
  );

  // ── Add Single Rx ──
  const handleAddSingleRx = useCallback(
    async (rx: any) => {
      // Find a matching bill by payer
      // For now, add to the first bill (user selects which via spotlight)
      try {
        const response = await transaction.loadSingleGkvBillRx(rx.id);
        const singleRxData = response as any;
        // Find bill matching the payer
        const matchBill = bills.find((b) => b.payer?.ik === singleRxData.payerIK);
        if (!matchBill) {
          notificationStore.showMessage(
            "Kein passender Payer für dieses Rezept gefunden",
            "warning",
          );
          return;
        }
        await addRx(matchBill.id, singleRxData.rx);
        notificationStore.showMessage("Rezept hinzugefügt", "success");
      } catch (error: any) {
        notificationStore.showMessage(`${error.message}`, "warning");
      }
      setIsSpotlightOpen(false);
    },
    [bills, transaction, addRx],
  );

  // ── Test Toggle ──
  const handleTestToggleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setTestToggleChecked(isChecked);
    if (isChecked) {
      // Open dialog to pick test type + email
      setOpenTestTypeDialog(true);
    } else {
      // Reset to production mode
      setTestTypeData({ type: TestType.GLOBAL });
    }
  }, []);

  // ── Header Actions ──
  const handleSave = useCallback(async () => {
    flushAll();
    await saveAll(bills, notes);
  }, [flushAll, saveAll, bills, notes]);

  const handlePrint = useCallback(async () => {
    const result = await printAllBatch(bills);
    if (result && result.failureCount === 0) {
      // Patch each bill's PDF links with the freshly generated document IDs
      // so the download buttons point at the new PDFs, not stale ones.
      result.results.forEach((jobResult) => {
        if (!jobResult.success) return;
        const paperDocumentId = (jobResult as any).paperDocumentId;
        const billDocumentId = (jobResult as any).billDocumentId;
        if (!paperDocumentId && !billDocumentId) return;
        updateBillLocally(jobResult.entityId, {
          ...(paperDocumentId ? { paperCenterReceipt: { id: paperDocumentId } as any } : {}),
          ...(billDocumentId ? { billPdf: { id: billDocumentId } as any } : {}),
        });
      });

      // All PDFs generated successfully — mark print as completed
      printedRxSnapshotRef.current = bills
        .flatMap((b) => b.rxs ?? [])
        .map((rx) => rx.id)
        .sort()
        .join(",");
      setIsPrintCompleted(true);
    }
  }, [printAllBatch, bills, updateBillLocally]);

  const handleExportClick = useCallback(async () => {
    const exportData: TestTypeData = testTypeData;

    const response = await exportAll(
      bills.map((b) => b.id),
      exportData,
    );

    // Show results summary dialog
    setExportResults(response.results);
  }, [testTypeData, exportAll, bills]);

  const handleTestTypeConfirm = useCallback((data: TestTypeData) => {
    setOpenTestTypeDialog(false);
    setTestTypeData(data);
  }, []);

  const handleExportResultsClose = useCallback(() => {
    setExportResults(null);
    onClose(true);
  }, [onClose]);

  // ── Render ──
  const backdropMessage = isPrinting
    ? "Es wird erstellt..."
    : isExporting
      ? "Bitte warten. Exportieren..."
      : isSaving
        ? "Bitte warten. Speichern..."
        : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "95vh" }}>
      <WaitingBackdrop
        isWorking={isPrinting || isExporting || isSaving}
        message={backdropMessage}
        progress={isPrinting ? printProgress : undefined}
      />

      {/* Sticky header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: colors.darkBlue,
          borderRadius: "0 0 12px 12px",
          px: 1.5,
          py: 0.75,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <GkvEditHeaderActions
          activeStep={activeStep}
          isValidating={isValidating}
          validationProgress={validationProgress}
          testToggleChecked={testToggleChecked}
          onAddRx={() => setIsSpotlightOpen(true)}
          onTestToggleChange={handleTestToggleChange}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <GkvEditSummary bills={bills} validationMap={validationMap} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip
              title={isPrintCompleted ? "Bereits Erstellt" : "Urbelegbegleitschreiben"}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handlePrint}
                  endIcon={
                    isPrintCompleted ? (
                      <TaskIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  disabled={!bills.length || isPrinting || isExporting || isSaving || isValidating}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: isPrintCompleted ? "#546e7a" : "#6ab187",
                    color: "#fff",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: isPrintCompleted ? "#546e7a" : "#5aa97f" },
                    "&:disabled": { backgroundColor: "#546e7a", color: "#90a4ae" },
                  }}
                >
                  {isPrintCompleted ? "Erstellt" : "Weiter"}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              onClick={handleExportClick}
              endIcon={<IosShareIcon sx={{ fontSize: 16 }} />}
              disabled={
                !bills.length ||
                isPrinting ||
                isExporting ||
                isSaving ||
                isValidating ||
                !isVersendenEnabled
              }
              sx={{
                borderRadius: 2,
                backgroundColor: "#6ab187",
                color: "#fff",
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                fontSize: "0.8rem",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#5aa97f" },
                "&:disabled": { backgroundColor: "#546e7a", color: "#90a4ae" },
              }}
            >
              Versenden
            </Button>
          </Box>
        </Box>
        <GkvEditBulkToolbar
          selectedCount={selectedRxIds.size}
          onDelete={handleBulkDelete}
          onClear={clearRxSelection}
        />
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 4 }}>
          <CircularProgress color="primary" />
          <Chip label="Daten werden geladen..." />
        </Box>
      )}

      {/* Bill cards */}
      {!isLoading &&
        bills.map((bill) => (
          <Box key={bill.id} sx={{ mb: 1, mt: 1 }}>
            <GkvEditBillCard
              bill={bill}
              selectedRxId={selectedRxId}
              selectedRxIds={selectedRxIds}
              validationMap={validationMap}
              note={notes[bill.id] || ""}
              onNoteChange={(val) => handleNoteChange(bill.id, val)}
              onRowClick={handleRowClick}
              onMenuOpen={handleMenuOpen}
              onRemoveAll={handleRemoveAll}
              onTaxieren={(rx) => taxieren.printTaxieren(rx, bill)}
              onToggleRxSelection={toggleRxSelection}
            />
          </Box>
        ))}

      {!isLoading && bills.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          Keine Rechnungen gefunden.
        </Box>
      )}

      {/* SpotlightSearch for adding single Rx */}
      <SpotlightSearch
        open={isSpotlightOpen}
        onClose={() => setIsSpotlightOpen(false)}
        onRxSelect={handleAddSingleRx}
      />

      {/* Delete Rx dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={() => {
          setOpenDeleteDialog(false);
          setMenuRx(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Rezept aus der Abrechnung entfernen?"
      />

      {/* Soft delete bill dialog */}
      <DeleteConfirmationDialog
        open={openSoftDeleteDialog}
        onCancel={() => {
          setOpenSoftDeleteDialog(false);
          setMenuRx(null);
        }}
        onConfirm={handleSoftDeleteConfirm}
        title="Gesamte Abrechnung löschen? (Letztes Rezept)"
      />

      {/* Cancel taxieren dialog */}
      <DeleteConfirmationDialog
        open={openCancelTaxierenDialog}
        onCancel={() => {
          setOpenCancelTaxierenDialog(false);
          setMenuRx(null);
        }}
        onConfirm={handleCancelTaxierenConfirm}
        title="Taxieren stornieren?"
      />

      {/* Row context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleTaxierenClick} disabled={menuRx?.isTaxieren}>
          Taxieren
        </MenuItem>
        <MenuItem onClick={handleCancelTaxierenClick} disabled={!menuRx?.isTaxieren}>
          Untaxieren
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          Entfernen
        </MenuItem>
      </Menu>

      {/* Test type selection dialog */}
      <TestTypeDialog
        open={openTestTypeDialog}
        onClose={() => setOpenTestTypeDialog(false)}
        onConfirm={handleTestTypeConfirm}
      />

      {/* Export results summary dialog */}
      <ExportResultsDialog
        open={exportResults !== null}
        results={exportResults ?? []}
        onClose={handleExportResultsClose}
      />

      {/* Print overlay (hidden) */}
      <GkvEditPrintOverlay
        printData={taxieren.printData}
        printRef={taxieren.printRef}
        onAfterPrint={taxieren.onAfterPrint}
      />
    </Box>
  );
};

export default GkvEditWorkspace;
